import { Box, createStyles, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { CatalogRule, Filter } from '@repo/api-codegen';
import {
	useApiCreatePolicyRule,
	useApiUpdateAutomationFilterSettings,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Button } from '@repo/foundations';
import { useAuthUser } from '../../../api';
import { useUpdateAutomation } from '../../../api/hooks/automation';
import { useUpdatePolicyRule } from '../../../api/hooks/policy';
import { invertCatalogFilterOperators } from '../../../utils/filterUtils';
import { useCreatePolicy, useUpdatePolicy } from '../../PoliciesPage/hooks';
import { useCreatePolicyStore } from './useCreatePolicyStore';

const useStyles = createStyles((theme) => ({
	stickyFooter: {
		position: 'fixed' as const,
		bottom: 0,
		left: 0,
		width: '100%',
		backgroundColor: `${theme.other.getColor('surface/primary/default')}`,
		borderTop: `${theme.other.borderWidth.sm}px solid ${theme.other.getColor('border/primary/default')}`,
		padding: `${theme.spacing.md} calc((100vw - ${theme.other.width.lg}px)/2) ${theme.spacing.md} 0`,
	},
	stickyPlaceholder: {
		height: theme.spacing.md,
	},
}));

function invertFilters(filters: Filter[]): CatalogRule {
	if (filters.length === 0) {
		return {
			operator: 'or',
			operands: [],
			field: null,
			value: null,
			sub_value: null,
		} as CatalogRule;
	}

	const invertedFilters = filters.map((filter) =>
		invertCatalogFilterOperators(filter)
	);

	return {
		operator: 'or',
		operands: invertedFilters,
		field: null,
		value: null,
		sub_value: null,
	} as CatalogRule;
}

interface CreatePolicyButtonProps {
	exitPage: () => void;
	policyId?: string;
}

function CreatePolicyButton({ exitPage, policyId }: CreatePolicyButtonProps) {
	const store = useCreatePolicyStore();
	const { user, workspace } = useAuthUser();
	const { classes } = useStyles();
	const { mutateAsync: createPolicy } = useCreatePolicy({});
	const { mutateAsync: updatePolicy } = useUpdatePolicy({});
	const { mutateAsync: updateRule } = useUpdatePolicyRule();
	const { mutateAsync: updateAutomation } = useUpdateAutomation({});
	const { mutateAsync: createPolicyRule } = useApiCreatePolicyRule({});
	const { mutateAsync: updateAutomationFilterSettings } =
		useApiUpdateAutomationFilterSettings();

	const handleCreate = async () => {
		if (!store.isFormValid) {
			return;
		}

		try {
			if (policyId) {
				// Update policy - policy rules are handled immediately in PolicyRulesStack
				await updatePolicy({
					pathParams: { policyId },
					body: store.getData(),
				});

				// Then update policy rule if rule is pii scan
				const piiScanPolicyRuleIndex = store
					.getPolicyRules()
					.findIndex((rule) => rule.type === 'PII_SCAN');
				if (piiScanPolicyRuleIndex !== -1) {
					const existingRule = store.initialPolicyRules[piiScanPolicyRuleIndex];
					if (existingRule?.id) {
						await updateRule({
							pathParams: { policyId, ruleId: existingRule.id },
							body: store.getPolicyRules()[piiScanPolicyRuleIndex],
						});
					}
				}

				// Update automation filters if automation exists and has policy rules
				if (store.automationId && store.policyRules.length > 0) {
					const policyRuleFilters = store.policyRules.map(
						(rule) => rule.filter
					);
					const invertedFilter: CatalogRule = invertFilters(policyRuleFilters);

					await updateAutomationFilterSettings({
						pathParams: { automationId: store.automationId },
						body: { filter_settings: invertedFilter },
					});
				}

				showNotification({
					title: 'Success',
					message: 'Policy updated successfully.',
					color: 'green',
				});
				trackEvent('policy/update', {}, user, workspace);
				if (
					store.automationId &&
					store.automationName !== store?.initialAutomationName
				) {
					await updateAutomation({
						data: {
							id: store.automationId,
							title: store.automationName,
						},
					});
				}
			} else {
				// Create policy first
				const createdPolicy = await createPolicy({
					body: {
						...store.getData(),
					},
				});

				// Then create all policy rules
				if (store.getPolicyRules().length > 0) {
					for (const policyRule of store.getPolicyRules()) {
						await createPolicyRule({
							pathParams: { policyId: createdPolicy.id },
							body: policyRule,
						});
					}
				}

				// Update automation filters if automation exists and has policy rules
				if (store.automationId && store.policyRules.length > 0) {
					const policyRuleFilters = store.policyRules.map(
						(rule) => rule.filter
					);
					const invertedFilter: CatalogRule = invertFilters(policyRuleFilters);

					await updateAutomationFilterSettings({
						pathParams: { automationId: store.automationId },
						body: { filter_settings: invertedFilter },
					});
				}

				showNotification({
					title: 'Success',
					message: 'New policy created successfully.',
					color: 'green',
				});
				trackEvent('policy/create', {}, user, workspace);
				if (
					store.automationId &&
					store.automationName !== store?.initialAutomationName
				) {
					await updateAutomation({
						data: {
							id: store.automationId,
							title: store.automationName,
						},
					});
				}
			}
			exitPage();
		} catch (error) {
			showNotification({
				message: `Failed to ${policyId ? 'update' : 'create'} policy`,
				color: 'red',
			});
		}
	};

	return (
		<Box className={policyId ? classes.stickyPlaceholder : undefined}>
			<Group
				position="right"
				className={policyId ? classes.stickyFooter : undefined}
			>
				<Button variant="primary" onClick={handleCreate}>
					{policyId ? 'Update policy' : 'Create policy'}
				</Button>
			</Group>
		</Box>
	);
}

export default CreatePolicyButton;
